import { useEffect, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import type { ErrorComponentProps } from "@tanstack/react-router";
import {
    Card,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from "@/components/ui/card.tsx";
import { Button } from "@/components/ui/button.tsx";

export function RootErrorPage({ error, reset }: ErrorComponentProps) {
    const [traceId, setTraceId] = useState<string | null>(null);
    const lastTraceIdRef = useRef<string | undefined>(undefined);

    useEffect(() => {
        const fallbackEventId =
            typeof Sentry.lastEventId === "function"
                ? Sentry.lastEventId()
                : undefined;

        if (fallbackEventId && fallbackEventId !== lastTraceIdRef.current) {
            lastTraceIdRef.current = fallbackEventId;
            setTraceId(fallbackEventId);
            return;
        }

        const eventId = Sentry.captureException(error);
        lastTraceIdRef.current = eventId ?? undefined;
        setTraceId(eventId ?? fallbackEventId ?? null);
    }, [error]);

    console.error(error);

    return (
        <div className="h-full flex items-center justify-center p-4">
            <Card className="bg-secondary w-full max-w-xl text-center">
                <CardHeader className="space-y-4">
                    <CardTitle className="uppercase">
                        Something went wrong
                    </CardTitle>
                    <CardDescription>
                        We are already working on this error. If you open a
                        ticket, please include this trace ID so we can fix it
                        faster.
                    </CardDescription>
                    <div className="text-sm font-mono text-muted-foreground">
                        {traceId
                            ? `Trace ID: ${traceId}`
                            : "Collecting trace id..."}
                    </div>
                </CardHeader>
                <CardFooter className="flex flex-col gap-3 lg:flex-row lg:justify-center">
                    <Button
                        className="uppercase"
                        size="lg"
                        variant="outline"
                        onClick={() => reset()}>
                        Try again
                    </Button>
                </CardFooter>
            </Card>
        </div>
    );
}
